import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as clientErrTypes from 'config/clientErrorTypes';
import { getWinner, checkIsQualifyToGetWinner } from 'api/client';
import {
    lotteryInstantTypes,
    lotteryParticipantTypes,
    lotteryAdminTypes
} from 'config/promoLotteryTypes';
import { isGetWinnerEmptyContinueError } from 'client/utils/errorObjectChecker';
import {
    setHasNewUnlockTaskPrize,
    setLotteryTaskMappingEntryInited,
    setLotteryTaskMappingEntry
} from 'client/game_checkIn/redux/modules/gameState';
import { statuses } from 'editor/game_checkIn/components/lotteryTaskCarousel/LotteryTaskCard';
import getLotteryCardRemainderInfo from 'client/game_checkIn/getLotteryCardRemainderInfo';
import { scrollElementIntoView } from 'client/utils/scroll';
import { gameTypes } from 'config/promoGameTypes';
import { scrollPromoBodyIntoView } from 'client/utils/scroll';

import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import useClientHistory, { LOTTERY } from 'client/hooks/useClientHistory';
import useSortedLotteriesTask from 'client/game_checkIn/hooks/useSortedLotteriesTask';
import useLotteriesTaskPrizeRemainder from 'client/game_checkIn/hooks/useLotteriesTaskPrizeRemainder';
import useLotteriesTaskUtilsState from 'client/game_checkIn/hooks/useLotteriesTaskUtilsState';
import useHasUserStateReady from 'client/game_checkIn/hooks/useHasUserStateReady';
import useLotteryPrizeDataManage from 'client/game_checkIn/hooks/useLotteryPrizeDataManage';
import {
    useCheckerContext,
    checkTypes
} from 'client/components/infoDialog/ProviderFlowInfoAndActionChecker';

import LotteryTaskProcessHint from 'client/game_checkIn/LotteryTaskProcessHint';
import LotteryTaskCarousel from 'editor/game_checkIn/components/lotteryTaskCarousel/LotteryTaskCarousel';
import EmptyTaskPrizePopup from 'client/game_checkIn/prizePopup/EmptyTaskPrizePopup';
import useLocalStorage from 'hooks/useLocalStorage';

const LotteryTaskCarouselContainer = forwardRef(
    (
        {
            deskTopSlidesToShow = 3,
            componentStyle,
            isNeedLotteryTaskProcessHint = true,
            inviteCount = 0
        },
        ref
    ) => {
        const [value, setLocalStorage] = useLocalStorage('handledAdminPrizeCards', []);
        // To persist this state across navigation
        const handleAdminPrizeRef = useRef(new Set(value));
        const dispatch = useDispatch();
        const history = useClientHistory();
        const errHandler = useClientErrorHandler();
        const checkerApi = useCheckerContext();
        const utilsState = useLotteriesTaskUtilsState();
        const hasUserStateReady = useHasUserStateReady();
        const lotteryPrizeDataManage = useLotteryPrizeDataManage({ onError: errHandler });
        const lotteryTasks = useSelector(s => s.lotteryTasks);
        const lotteryPrizes = useSelector(s => s.lotteryPrizes);
        const { isMgmShare, isMgmSelf } = useSelector(s => s.flags);
        const { lotteriesTaskCardInfo = {} } = useSelector(s => s.settingGame);
        const { uuid: promoUUID, type: gameType } = useSelector(s => s.promo);
        const { mgmReferral: { uuid: mgmPromoUUID } = {} } = useSelector(s => s.mgmRelation);
        const {
            settingGame: { lotteriesTaskCardInfo: mgmLotteriesTaskCardInfo = {} } = {},
            lotteryTasks: mgmLotteryTasks = {},
            lotteryPrizes: mgmLotteryPrizes = {}
        } = useSelector(s => s.mgmReferral);
        const {
            hasLotteryTaskMappingEntryInited = false,
            lotteryTaskMappingEntry = {}
        } = useSelector(s => s.gameState || {});
        const hasEnableSignupForm = useSelector(s => Boolean(s.signUpForm.id));

        const carouselRef = useRef(null);
        const stateRef = useRef({
            hasCarouselInitSlide: false
        });

        const [canStartLazyLoadCard, setCanStartLazyLoadCard] = useState(false);
        const [taskPrizeEmptyUUID, setTaskPrizeEmptyUUID] = useState('');

        const sortedLotteriesTask = useSortedLotteriesTask(lotteryTasks);
        const sortedLotteriesTaskForMgm = useSortedLotteriesTask(mgmLotteryTasks);
        const {
            lotteriesRemainder,
            setLotteryRemainder,
            hasInited: hasLotteriesRemainderInited
        } = useLotteriesTaskPrizeRemainder({
            promoUUID,
            mgmPromoUUID,
            lotteriesTask: sortedLotteriesTask,
            mgmLotteriesTask: sortedLotteriesTaskForMgm,
            onError: errHandler
        });

        const currentLotteryPrizes = { ...lotteryPrizes, ...mgmLotteryPrizes };
        const currentLotteriesTaskCardInfo = {
            ...lotteriesTaskCardInfo,
            ...mgmLotteriesTaskCardInfo
        };
        const currentSortedLotteriesTask = [...sortedLotteriesTask, ...sortedLotteriesTaskForMgm];
        const currentLotteryTasks = { ...lotteryTasks, ...mgmLotteryTasks };

        const canInitLotteryCardSlide = hasUserStateReady && hasLotteriesRemainderInited;
        const cards = currentSortedLotteriesTask.map(lotteryInfo => {
            const { uuid, taskCondition } = lotteryInfo;
            const isMgmLotteriesTask = sortedLotteriesTaskForMgm.find(item => item.uuid === uuid);
            const lotteryRemainder = lotteriesRemainder[uuid] ?? {};
            const lottery = { ...lotteryInfo, ...lotteryRemainder };
            const prizes = currentLotteryPrizes[uuid] ?? [];
            const { prizeLeftQuantity, prizeTotalQuantity } = getLotteryCardRemainderInfo(
                lottery,
                prizes
            );
            const { type, prizeLimited, remainder } = lottery;
            const [title, imgSrc] = (() => {
                let { title: t, mainViewImg } = currentLotteriesTaskCardInfo[uuid] ?? {};
                if (type === lotteryInstantTypes.JOIN_AND_GET && prizes.length === 1) {
                    t = t ?? prizes[0].name;
                    mainViewImg = mainViewImg ?? prizes[0].settings?.imageUrl;
                }
                return [t, mainViewImg];
            })();
            const status = (() => {
                if (isMgmLotteriesTask) {
                    if (utilsState.isAllGameResultRecordsBeDeleted) {
                        return statuses.LOCKED;
                    }
                }
                const lotteryTaskWinnerInfo = utilsState.lotteriesTaskWinnerInfo[uuid];
                if (lotteryTaskWinnerInfo.hasGetWinner) {
                    if (lotteryTaskWinnerInfo?.entryFlow?.deletedAt) {
                        return statuses.IS_UN_QUALIFY;
                    } else {
                        return statuses.IS_FULFILL;
                    }
                } else {
                    if (prizeLimited && remainder === 0 && type !== lotteryAdminTypes.ADMIN) {
                        return statuses.PRIZE_EMPTY;
                    } else {
                        const mappingEntry =
                            utilsState.entriesRecords.byUUIDs[lotteryTaskMappingEntry[uuid]];
                        if (mappingEntry) {
                            const { deletedAt } = mappingEntry;
                            if (Boolean(deletedAt)) {
                                return statuses.IS_UN_QUALIFY;
                            } else {
                                return type === lotteryAdminTypes.ADMIN
                                    ? statuses.UNLOCKED_ADMIN_PRIZE
                                    : statuses.UNLOCKED;
                            }
                        }
                        return statuses.LOCKED;
                    }
                }
            })();
            return {
                uuid,
                type,
                title,
                imgSrc,
                taskCondition,
                prizeLeftQuantity,
                prizeTotalQuantity,
                status,
                isIniting: !canStartLazyLoadCard,
                isLazyLoading:
                    canStartLazyLoadCard && !lotteryPrizeDataManage.isLotteryHasLoadPrizeData(uuid),
                isMgmSelf: isMgmLotteriesTask ? isMgmSelf : false,
                isMgmShare: isMgmLotteriesTask ? isMgmShare : false
            };
        });

        useEffect(() => {
            if (canInitLotteryCardSlide) {
                if (!hasLotteryTaskMappingEntryInited) {
                    dispatch(
                        setLotteryTaskMappingEntry(
                            getLotteryTaskMappingEntry(utilsState, inviteCount)
                        )
                    );
                    dispatch(setLotteryTaskMappingEntryInited());
                } else {
                    setCanStartLazyLoadCard(true);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [canInitLotteryCardSlide, hasLotteryTaskMappingEntryInited]);

        //要去更新假設inviteCount改變
        useEffect(() => {
            if (canInitLotteryCardSlide) {
                if (hasLotteryTaskMappingEntryInited) {
                    dispatch(
                        setLotteryTaskMappingEntry(
                            getLotteryTaskMappingEntry(utilsState, inviteCount)
                        )
                    );
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [canInitLotteryCardSlide, inviteCount]);

        useImperativeHandle(ref, () => ({
            updateTaskStatus: handleRenewRecords,
            getNextCheckPointLotteryTaskInfo,
            scrollCardsIntoView
        }));

        function checkCanGetTaskWinner() {
            return checkerApi.check([checkTypes.PROMO_STATUS]);
        }
        async function handleGetTaskWinner({ uuid, status, type, isMgmSelf, isMgmShare }) {
            try {
                if (checkCanGetTaskWinner() && status === statuses.UNLOCKED) {
                    const currentPromoUUID = Boolean(isMgmSelf || isMgmShare)
                        ? mgmPromoUUID
                        : promoUUID;
                    const entryUUID = lotteryTaskMappingEntry[uuid];

                    if (type === lotteryParticipantTypes.PICK_THE_PRIZE) {
                        scrollPromoBodyIntoView();
                        const { AnimationSettings = {} } = currentLotteryTasks[uuid] ?? {};
                        history.push(LOTTERY, {
                            animatingLottery: { uuid, type, AnimationSettings },
                            entriesUUID: entryUUID
                        });
                    } else {
                        await checkIsQualifyToGetWinner({
                            promoUUID: currentPromoUUID,
                            entriesUUID: entryUUID,
                            lotteryUUID: uuid
                        });
                        const winnerResp = await getWinner(currentPromoUUID, entryUUID, uuid);
                        const { httpStatus, payload } = winnerResp;
                        if (httpStatus === 200) {
                            scrollPromoBodyIntoView();
                            const prizeUUID = payload[0];
                            const { type, AnimationSettings } = currentLotteryTasks[uuid] ?? {};
                            history.push(LOTTERY, {
                                animatingLottery: { type, uuid, AnimationSettings },
                                entriesUUID: entryUUID,
                                prizeUUID
                            });
                        } else if (isGetWinnerEmptyContinueError(winnerResp)) {
                            setTaskPrizeEmptyUUID(uuid);
                        } else if (httpStatus === 400) {
                            if (
                                payload.message === clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK
                            ) {
                                throw generateApiErrObj(
                                    winnerResp,
                                    clientErrTypes.POINTS_IS_NOT_ENOUGH_BY_PRE_CHECK
                                );
                            } else if (payload.message === clientErrTypes.POINTS_IS_NOT_ENOUGH) {
                                throw generateApiErrObj(
                                    winnerResp,
                                    clientErrTypes.POINTS_IS_NOT_ENOUGH
                                );
                            } else {
                                throw generateApiErrObj(winnerResp, clientErrTypes.GET_WINNER_TASK);
                            }
                        } else {
                            throw generateApiErrObj(winnerResp, clientErrTypes.GET_WINNER_TASK);
                        }
                    }
                }
            } catch (err) {
                errHandler({
                    err
                });
            } finally {
                dispatch(setHasNewUnlockTaskPrize(false));
            }
        }

        function getLotteryTaskMappingEntry(utilsState, inviteCount) {
            const { entriesRecords } = utilsState;
            const entriesRecordsByUUIDsForMgm = Object.keys(entriesRecords.byUUIDs).reduce(
                (acc, key) => {
                    if (entriesRecords.byUUIDs[key].isMgm === true) {
                        acc[key] = entriesRecords.byUUIDs[key];
                    }
                    return acc;
                },
                {}
            );
            const entriesRecordsByUUIDs = Object.keys(entriesRecords.byUUIDs).reduce((acc, key) => {
                if (entriesRecords.byUUIDs[key].isMgm !== true) {
                    acc[key] = entriesRecords.byUUIDs[key];
                }
                return acc;
            }, {});

            switch (gameType) {
                case gameTypes.CHECK_IN: {
                    return cards.reduce((obj, c) => {
                        if (Boolean(c.isMgmSelf || c.isMgmShare)) {
                            if (
                                c.status === statuses.LOCKED ||
                                c.status === statuses.UNLOCKED ||
                                c.status === statuses.UNLOCKED_ADMIN_PRIZE
                            ) {
                                if (inviteCount >= c.taskCondition) {
                                    const entryRecord =
                                        entriesRecordsByUUIDsForMgm[
                                            entriesRecords.allUUIDsForMgm[c.taskCondition - 1]
                                        ];
                                    if (entryRecord) {
                                        obj[c.uuid] = entryRecord.uuid;
                                    }
                                }
                            }
                            return obj;
                        }
                        const entryRecord =
                            entriesRecordsByUUIDs[entriesRecords.allUUIDs[c.taskCondition - 1]];
                        if (entryRecord) {
                            if (!hasEnableSignupForm || Boolean(entryRecord?.flow?.formSignUp)) {
                                obj[c.uuid] = entryRecord.uuid;
                            }
                        }
                        return obj;
                    }, {});
                }
                case gameTypes.CHALLENGE_PHOTO_FRAME_COMPOSITE:
                    let mapped = 0;
                    return cards.reduce((obj, c) => {
                        if (Boolean(c.isMgmSelf || c.isMgmShare)) {
                            if (
                                c.status === statuses.LOCKED ||
                                c.status === statuses.UNLOCKED ||
                                c.status === statuses.UNLOCKED_ADMIN_PRIZE
                            ) {
                                if (inviteCount >= c.taskCondition) {
                                    const entryRecord =
                                        entriesRecordsByUUIDsForMgm[
                                            entriesRecords.allUUIDsForMgm[c.taskCondition - 1]
                                        ];
                                    if (entryRecord) {
                                        obj[c.uuid] = entryRecord.uuid;
                                    }
                                }
                            }
                            return obj;
                        }
                        if (
                            c.status === statuses.LOCKED ||
                            c.status === statuses.UNLOCKED ||
                            c.status === statuses.UNLOCKED_ADMIN_PRIZE
                        ) {
                            if (entriesRecords.validEntryTotal >= c.taskCondition) {
                                const entryRecord =
                                    entriesRecords.byUUIDs[
                                        entriesRecords.allUUIDsValidToGetTaskWinner[mapped++]
                                    ];
                                if (entryRecord) {
                                    obj[c.uuid] = entryRecord.uuid;
                                }
                            }
                        }
                        return obj;
                    }, {});
                case gameTypes.MULTI_CHALLENGE:
                    let count = 0;
                    return cards.reduce((obj, c) => {
                        if (
                            c.status === statuses.LOCKED ||
                            c.status === statuses.UNLOCKED ||
                            c.status === statuses.UNLOCKED_ADMIN_PRIZE
                        ) {
                            if (entriesRecords.validEntryTotal >= c.taskCondition) {
                                const entryRecord =
                                    entriesRecords.byUUIDs[
                                        entriesRecords.allUUIDsValidToGetTaskWinner[count++]
                                    ];
                                if (entryRecord) {
                                    obj[c.uuid] = entryRecord.uuid;
                                }
                            }
                        }
                        return obj;
                    }, {});
                case gameTypes.MGM_SHARE:
                default: {
                    return cards.reduce((obj, c) => {
                        if (
                            c.status === statuses.LOCKED ||
                            c.status === statuses.UNLOCKED ||
                            c.status === statuses.UNLOCKED_ADMIN_PRIZE
                        ) {
                            if (inviteCount >= c.taskCondition) {
                                const entryRecord =
                                    entriesRecords.byUUIDs[
                                        entriesRecords.allUUIDsForMgm[c.taskCondition - 1]
                                    ];
                                if (entryRecord) {
                                    obj[c.uuid] = entryRecord.uuid;
                                }
                            }
                        }
                        return obj;
                    }, {});
                }
            }
        }

        function getNextCheckPointLotteryTaskInfo() {
            const newLotteryTaskMappingEntry = getLotteryTaskMappingEntry(utilsState, inviteCount);
            const newCheckPointTaskUUID = Object.keys(newLotteryTaskMappingEntry).find(
                taskUUID => lotteryTaskMappingEntry[taskUUID] === undefined
            );
            return {
                hasCheckPointTask: Boolean(newCheckPointTaskUUID),
                checkPointTaskUUID: newCheckPointTaskUUID,
                newLotteryTaskMappingEntry
            };
        }
        function handleRenewRecords() {
            const checkPointInfo = getNextCheckPointLotteryTaskInfo();

            if (checkPointInfo.hasCheckPointTask) {
                dispatch(setLotteryTaskMappingEntry(checkPointInfo.newLotteryTaskMappingEntry));
                const checkPointCardIndex = cards.findIndex(
                    c => c.uuid === checkPointInfo.checkPointTaskUUID
                );
                if (checkPointCardIndex !== -1) {
                    carouselGoTo(checkPointCardIndex);
                    if (cards[checkPointCardIndex].type !== lotteryAdminTypes.ADMIN) {
                        dispatch(setHasNewUnlockTaskPrize(true));
                    }
                }
                return checkPointCardIndex;
            }
            return -1;
        }
        function handleCardLazyLoaded(lotteryUUID, isMgm) {
            lotteryPrizeDataManage.load(lotteryUUID, isMgm);
            initSlideCarousel();
        }
        function initSlideCarousel() {
            if (!stateRef.current.hasCarouselInitSlide) {
                stateRef.current.hasCarouselInitSlide = true;
                if (cards.length > 1) {
                    let targetIndex = cards.findIndex(c =>
                        [statuses.LOCKED, statuses.UNLOCKED].includes(c.status)
                    );
                    targetIndex = targetIndex === -1 ? cards.length - 1 : targetIndex;
                    setTimeout(() => carouselGoTo(targetIndex), 500);
                }
            }
        }
        function carouselGoTo(index) {
            if (carouselRef.current) {
                carouselRef.current.slickGoTo(index);
            }
        }
        function scrollCardsIntoView() {
            const element = document.querySelector('.fever-lottery-task-cards');
            if (element) {
                scrollElementIntoView(element, 'end');
            }
        }

        useEffect(() => {
            const unlockedCards = cards.filter(card =>
                [statuses.UNLOCKED, statuses.UNLOCKED_ADMIN_PRIZE].includes(card.status)
            );
            if (unlockedCards.length > 0) {
                // Check if we have any new UNLOCKED_ADMIN_PRIZE cards that we haven't handled
                const newAdminPrizeCards = unlockedCards.filter(
                    card =>
                        card.status === statuses.UNLOCKED_ADMIN_PRIZE &&
                        !handleAdminPrizeRef.current.has(card.uuid)
                );

                // If we have new admin prize cards, mark them as handled
                newAdminPrizeCards.forEach(card => {
                    handleAdminPrizeRef?.current.add(card.uuid);
                    setLocalStorage([...handleAdminPrizeRef.current]);
                });

                // Only scroll if we have regular UNLOCKED cards or new UNLOCKED_ADMIN_PRIZE cards
                if (
                    unlockedCards.some(card => card.status === statuses.UNLOCKED) ||
                    newAdminPrizeCards.length > 0
                ) {
                    scrollCardsIntoView();
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [lotteryTaskMappingEntry]);

        return (
            <>
                <EmptyTaskPrizePopup
                    open={Boolean(taskPrizeEmptyUUID)}
                    onClose={() => {
                        setTaskPrizeEmptyUUID('');
                        setLotteryRemainder({ uuid: taskPrizeEmptyUUID, remainder: 0 });
                    }}
                />
                {isNeedLotteryTaskProcessHint && (
                    <LotteryTaskProcessHint
                        gameType={gameType}
                        cards={cards}
                        recordsLength={utilsState.entriesRecords.validEntryTotal}
                        componentStyle={componentStyle}
                    />
                )}
                <LotteryTaskCarousel
                    gameType={gameType}
                    deskTopSlidesToShow={deskTopSlidesToShow}
                    forwardCarouselRef={carouselRef}
                    disabledEdit
                    items={cards}
                    componentStyle={componentStyle}
                    onLazyLoaded={handleCardLazyLoaded}
                    onGetWinner={handleGetTaskWinner}
                />
            </>
        );
    }
);

LotteryTaskCarouselContainer.displayName = 'LotteryTaskCarouselContainer';

export default React.memo(LotteryTaskCarouselContainer);
